import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useCalendars } from './CalendarContext';
import {
    getGoals,
    getGoalsByPeriod,
    getGoalById,
    createGoal as createGoalStorage,
    updateGoal as updateGoalStorage,
    deleteGoal as deleteGoalStorage,
    getGoalProgress
} from '../services/goalStorage';

const GoalContext = createContext();

export const useGoals = () => {
    const context = useContext(GoalContext);
    if (!context) {
        throw new Error('useGoals must be used within a GoalProvider');
    }
    return context;
};

export const GoalProvider = ({ children }) => {
    const [goals, setGoals] = useState([]);
    const [selectedGoal, setSelectedGoal] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tasksVersion, setTasksVersion] = useState(0);
    const [filters, setFilters] = useState({
        period: null,
        year: new Date().getFullYear(),
        quarter: null,
        month: null,
        status: null
    });

    const { currentUser } = useAuth();
    const { selectedCalendar } = useCalendars();

    // Load goals for the selected calendar
    const loadGoals = useCallback(async () => {
        if (!currentUser || !selectedCalendar) {
            setGoals([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            let data;
            if (filters.period && filters.year) {
                // Use filtered query if period is set
                data = await getGoalsByPeriod(
                    selectedCalendar.id,
                    filters.period,
                    filters.year,
                    filters.quarter,
                    filters.month
                );
            } else {
                // Get all goals for the calendar
                data = await getGoals(selectedCalendar.id, currentUser.uid);
            }

            // Apply status filter if set
            if (filters.status) {
                data = data.filter(goal => goal.status === filters.status);
            }

            setGoals(data);
        } catch (err) {
            console.error('Error loading goals:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentUser, selectedCalendar, filters]);

    // Reload goals when calendar or filters change
    useEffect(() => {
        loadGoals();
    }, [loadGoals]);

    // Create a new goal
    const createGoal = async (goalData) => {
        if (!currentUser || !selectedCalendar) {
            throw new Error('User must be logged in and a calendar must be selected');
        }

        try {
            const newGoal = await createGoalStorage(
                { ...goalData, calendarId: selectedCalendar.id },
                currentUser.uid
            );

            setGoals(prev => [newGoal, ...prev]);
            return newGoal;
        } catch (err) {
            console.error('Error creating goal:', err);
            throw err;
        }
    };

    // Update a goal
    const updateGoal = async (goalId, updates) => {
        if (!currentUser) {
            throw new Error('User must be logged in to update goals');
        }

        try {
            const updatedGoal = await updateGoalStorage(goalId, updates, currentUser.uid);

            setGoals(prev =>
                prev.map(goal => goal.id === goalId ? updatedGoal : goal)
            );

            if (selectedGoal?.id === goalId) {
                setSelectedGoal(updatedGoal);
            }

            return updatedGoal;
        } catch (err) {
            console.error('Error updating goal:', err);
            throw err;
        }
    };

    // Delete a goal
    const deleteGoal = async (goalId) => {
        if (!currentUser) {
            throw new Error('User must be logged in to delete goals');
        }

        try {
            await deleteGoalStorage(goalId, currentUser.uid);

            setGoals(prev => prev.filter(goal => goal.id !== goalId));

            if (selectedGoal?.id === goalId) {
                setSelectedGoal(null);
            }
        } catch (err) {
            console.error('Error deleting goal:', err);
            throw err;
        }
    };

    // Get goal progress
    const getProgress = async (goalId) => {
        if (!selectedCalendar) {
            throw new Error('No calendar selected');
        }

        try {
            return await getGoalProgress(goalId, selectedCalendar.id);
        } catch (err) {
            console.error('Error getting goal progress:', err);
            throw err;
        }
    };

    // Get goal by ID
    const getGoal = async (goalId) => {
        if (!currentUser) {
            throw new Error('User must be logged in');
        }

        try {
            return await getGoalById(goalId, currentUser.uid);
        } catch (err) {
            console.error('Error getting goal:', err);
            throw err;
        }
    };

    // Signal that tasks associated with goals have changed (triggers GoalCard progress refresh)
    const notifyTaskChanged = useCallback(() => {
        setTasksVersion(v => v + 1);
    }, []);

    // Update filters
    const updateFilters = (newFilters) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
    };

    // Clear filters
    const clearFilters = () => {
        setFilters({
            period: null,
            year: new Date().getFullYear(),
            quarter: null,
            month: null,
            status: null
        });
    };

    // Refresh goals
    const refreshGoals = useCallback(async () => {
        await loadGoals();
    }, [loadGoals]);

    // Get goals grouped by period
    const getGroupedGoals = useCallback(() => {
        const grouped = {
            yearly: [],
            quarterly: [],
            monthly: []
        };

        goals.forEach(goal => {
            if (grouped[goal.period]) {
                grouped[goal.period].push(goal);
            }
        });

        return grouped;
    }, [goals]);

    // Get goals statistics
    const getStats = useCallback(() => {
        const total = goals.length;
        const active = goals.filter(g => g.status === 'active').length;
        const completed = goals.filter(g => g.status === 'completed').length;
        const archived = goals.filter(g => g.status === 'archived').length;

        return {
            total,
            active,
            completed,
            archived
        };
    }, [goals]);

    const value = {
        goals,
        selectedGoal,
        setSelectedGoal,
        loading,
        error,
        filters,
        tasksVersion,
        notifyTaskChanged,
        createGoal,
        updateGoal,
        deleteGoal,
        getProgress,
        getGoal,
        updateFilters,
        clearFilters,
        refreshGoals,
        getGroupedGoals,
        getStats
    };

    return (
        <GoalContext.Provider value={value}>
            {children}
        </GoalContext.Provider>
    );
};

export default GoalContext;
